import qbLogo from "@/assets/QB_transparentBG.png";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Display-only QuickBooks logo mark shown alongside the status pill when an
 * order has been synced to QuickBooks. Replaces the Phase 2.5 "Sent to QB"
 * text pill while preserving the two-fact rule (status and QB-sync remain
 * visually distinct — a cancelled-and-synced order shows the cancelled pill
 * AND this mark, never one merged "Sent to QB" pill).
 *
 * Renders nothing when syncedAt is nullish (un-synced order).
 *
 * Relies on the app-root <TooltipProvider> in App.tsx.
 */
interface QbSyncedMarkProps {
  syncedAt: string | Date | null | undefined;
  /** Display square size in px. Default 16 — keep within 16–18px range. */
  size?: number;
  className?: string;
}

export function QbSyncedMark({ syncedAt, size = 16, className }: QbSyncedMarkProps) {
  if (!syncedAt) return null;

  const parsed = typeof syncedAt === "string" || typeof syncedAt === "number"
    ? new Date(syncedAt)
    : syncedAt;
  const dateStr = parsed instanceof Date && !isNaN(parsed.getTime())
    ? parsed.toLocaleDateString()
    : null;
  const tooltipLabel = dateStr ? `Sent to QuickBooks · ${dateStr}` : "Sent to QuickBooks";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <img
          src={qbLogo}
          alt="Sent to QuickBooks"
          width={size}
          height={size}
          style={{ width: size, height: size }}
          className={`inline-block object-contain cursor-default select-none ${className ?? ""}`}
          draggable={false}
          data-testid="qb-synced-mark"
        />
      </TooltipTrigger>
      <TooltipContent>{tooltipLabel}</TooltipContent>
    </Tooltip>
  );
}
